import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { Phone, ShieldCheck, Truck, BadgeCheck, Clock } from "lucide-react";
import { LeadForm } from "./LeadForm";
import { Eyebrow } from "./Reveal";
import { EASE, PHONE_TEL, PHONE_DISPLAY } from "./data";

const LINES = [
  [{ t: "Magyar Extrém" }],
  [{ t: "Takarítás –", c: "text-[var(--crimson)]" }],
  [{ t: "Szakszerű, Diszkrét és Gyors", small: true }],
  [{ t: "Mentesítés Hazai Szakemberektől", small: true }],
];

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.15 + i * 0.12, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const TRUST = [
  { icon: ShieldCheck, t: "100% fix ár" },
  { icon: Truck, t: "Jelzés nélküli autók" },
  { icon: Clock, t: "Visszahívás 1 órán belül" },
];

const HeroVisual = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div ref={ref} className="relative [perspective:1200px]" onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
      <motion.div
        style={{ rotateX: rx, rotateY: ry }}
        initial={{ clipPath: "inset(100% 0% 0% 0% round 28px)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
        transition={{ duration: 1.4, delay: 0.35, ease: EASE }}
        className="relative aspect-[4/5] w-full overflow-hidden rounded-[28px] bg-[var(--navy)] shadow-[0_40px_80px_-30px_rgba(15,42,74,0.55)] [transform-style:preserve-3d]"
      >
        <motion.img
          style={{ y: imgY }}
          src="/img/hero_team.webp"
          alt="Hazai szakember sötétkék munkaruhában egy fertőtlenített, üres lakásban"
          className="absolute inset-0 h-[115%] w-full scale-105 object-cover"
        />
        <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_30%_10%,transparent_40%,rgba(10,29,51,0.55)_100%)]" />
        <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-white backdrop-blur-md">
          <span className="text-sm font-semibold">Diszkrét, szakszerű mentesítés</span>
          <BadgeCheck className="h-5 w-5" />
        </div>
      </motion.div>
      <div className="pointer-events-none absolute -left-8 -top-8 hidden h-32 w-32 lg:block">
        <svg viewBox="0 0 100 100" className="spin-slow h-full w-full">
          <defs><path id="circ" d="M50 50 m-38 0 a38 38 0 1 1 76 0 a38 38 0 1 1 -76 0" /></defs>
          <circle cx="50" cy="50" r="49" fill="#C8102E" />
          <text fontSize="9.5" fontWeight="800" letterSpacing="2.2" fill="#fff" fontFamily="Archivo, sans-serif">
            <textPath href="#circ">100% FIX ÁR • REJTETT KÖLTSÉG NÉLKÜL •</textPath>
          </text>
        </svg>
        <span className="font-display absolute inset-0 flex items-center justify-center text-xl font-black text-white">100%</span>
      </div>
    </div>
  );
};

export const Hero = () => (
  <section id="top" data-testid="hero-section" className="grain relative overflow-hidden pt-[104px] sm:pt-[120px]">
    <div className="pointer-events-none absolute -right-40 top-0 h-[600px] w-[600px] rounded-full bg-[var(--navy)]/[0.05] blur-3xl" />
    <div className="relative mx-auto grid max-w-7xl gap-12 px-4 pb-16 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8 lg:pb-24">
      <div className="lg:col-span-7 lg:pt-6">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
          <Eyebrow>Hazai, megbízható szakembercsapat</Eyebrow>
        </motion.div>
        <h1 data-testid="hero-headline" className="font-display mt-6 font-black leading-[0.98] tracking-[-0.03em] text-[var(--navy)]">
          {LINES.map((line, i) => (
            <MaskLine key={i} i={i}>
              {line.map((w) => (
                <span key={w.t} className={`${w.small ? "text-[1.6rem] font-extrabold leading-[1.15] tracking-[-0.02em] sm:text-4xl lg:text-[2.6rem]" : "text-[2.45rem] sm:text-6xl lg:text-[5.4rem]"} ${w.c || ""}`}>{w.t}</span>
              ))}
            </MaskLine>
          ))}
        </h1>
        <motion.p
          data-testid="hero-subheadline"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
          className="mt-7 max-w-2xl text-base leading-relaxed text-[var(--muted)] sm:text-lg"
        >
          Messie-lakások, elhanyagolt ingatlanok és biológiai fertőtlenítés teljes körű lebonyolítása jelzés nélküli autókkal, rejtett költségek nélkül.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
        >
          <motion.a
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            href={PHONE_TEL}
            data-testid="hero-call-button"
            className="inline-flex items-center justify-center gap-3 rounded-full bg-[var(--cta)] px-7 py-4 text-lg font-bold text-white shadow-[0_18px_36px_-12px_rgba(220,68,32,0.8)] transition-colors hover:bg-[var(--cta-hover)]"
          >
            <Phone className="h-5 w-5" /> Hívás Most · {PHONE_DISPLAY}
          </motion.a>
          <a href="#ajanlat" data-testid="hero-quote-link" className="inline-flex items-center justify-center rounded-full border-2 border-[var(--navy)] px-7 py-[14px] text-base font-bold text-[var(--navy)] transition-[background-color,color] hover:bg-[var(--navy)] hover:text-white">
            Részletes ajánlatkérés
          </a>
        </motion.div>
        <motion.ul
          initial="h"
          animate="s"
          variants={{ s: { transition: { staggerChildren: 0.08, delayChildren: 1.05 } } }}
          className="mt-9 flex flex-wrap gap-2.5"
        >
          {TRUST.map(({ icon: I, t }) => (
            <motion.li key={t} variants={{ h: { opacity: 0, y: 10 }, s: { opacity: 1, y: 0 } }} className="inline-flex items-center gap-2 rounded-full border border-[var(--navy)]/12 bg-white/80 px-3.5 py-2 text-sm font-semibold text-[var(--navy)]">
              <I className="h-4 w-4 text-[var(--crimson)]" /> {t}
            </motion.li>
          ))}
        </motion.ul>
      </div>

      <div className="relative lg:col-span-5">
        <div className="grid gap-6 sm:grid-cols-2 lg:block">
          <HeroVisual />
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9, ease: EASE }}
            data-testid="hero-quick-quote"
            className="relative z-10 rounded-[24px] border border-black/5 bg-white p-6 shadow-[0_30px_60px_-25px_rgba(15,42,74,0.35)] lg:-mt-40 lg:ml-[-18%] lg:mr-6"
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--crimson)]">1 perces · bizalmas</p>
            <p className="font-display mb-4 mt-1 text-2xl font-extrabold text-[var(--navy)]">Díjmentes Ajánlatkérés</p>
            <LeadForm prefix="hero" compact />
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);
